import cn from 'classnames'
import { IProfilingCode } from 'legacy/lut/classes/models/profiling/codes'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { JSX, memo, ReactNode, useCallback, useMemo } from 'react'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { IForm } from 'core-components/classes/models/IForm'
import { Button } from 'core-components/library/button'
import { DatePicker } from 'core-components/library/datepicker'
import { DropdownDynamic } from 'core-components/library/dropdown'
import { LoaderOverlay } from 'core-components/library/loader'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { partnerFlagRequestTranslation } from '../config/translation'
import { checkCodeAvailability } from '../lib/checkCodeAvailability'
import { IPartnerFlagRequestProps } from '../model/types'
import { usePartnerFlagRequest } from '../model/usePartnerFlagRequest'

import { getWarningMessages } from './PartnerFlagRequestWarnings'

import styles from './PartnerFlagRequest.module.scss'

export const PartnerFlagRequest = memo<IPartnerFlagRequestProps>(
  ({ partner, partnerFlags, type, onApply, onClose }) => {
    /** ----------------------- Variables ---------------------- */

    const t = useSafetyTranslation()
    const {
      endDate,
      handleWarningCancel,
      handleWarningClose,
      hasRequiredRole,
      hasWarningCancel,
      inactivePartnerFlag,
      isApplyButtonDisabled,
      isLoading,
      isRequiredUser,
      isSelectedCodeAvailable,
      isStartDateLessThanCurrent,
      isStartDateMoreThanEndDate,
      isWarningVisible,
      onApplyClickHandler,
      onProfilingCodeSelect,
      selectedProfilingCode,
      setEndDate,
      setStartDate,
      source,
      startDate,
      userEmail,
      userRoles,
      warningConfig,
    } = usePartnerFlagRequest({ partner, partnerFlags, type, onApply })

    /** ----------------------- Computed Values ---------------------- */
    // Check if the user has the required role or is a super admin
    const messages = useMemo<ReactNode[]>(() => {
      if (selectedProfilingCode) {
        return getWarningMessages({
          profilingCode: selectedProfilingCode,
          inactivePartnerFlag: inactivePartnerFlag,
          hasRequiredRole,
          isRequiredUser,
          isStartDateLessThanCurrent,
          isStartDateMoreThanEndDate,
        })
      }

      return []
    }, [
      hasRequiredRole,
      inactivePartnerFlag,
      isRequiredUser,
      isStartDateLessThanCurrent,
      isStartDateMoreThanEndDate,
      selectedProfilingCode,
    ])

    const infoBlockContent = useMemo(() => {
      if (messages.length) {
        return (
          <div className={styles.infoBlock}>
            {messages}
            {!isSelectedCodeAvailable && (
              <Translation
                htmlAvailable
                text={partnerFlagRequestTranslation.notification.instruction}
              />
            )}
          </div>
        )
      }

      return null
    }, [isSelectedCodeAvailable, messages])

    /** ----------------------- Handles ---------------------- */
    const renderDropdownFn = useCallback(
      (profilingCode: IProfilingCode) => {
        const isCodeAvailable = checkCodeAvailability({
          profilingCode,
          userEmail,
          userRoles,
        })

        return (
          <div
            className={cn(styles.dropdownItem, {
              [styles.dropdownItem_warning]: !isCodeAvailable,
            })}
          >
            <div className={styles.dropdownItem_codeName}>
              {!isCodeAvailable && <i className="kbase kbase-warning-alt" />}
              {profilingCode.codeName}
            </div>
            <div className={styles.dropdownItem_code}>{profilingCode.code}</div>
          </div>
        )
      },
      [userEmail, userRoles]
    )

    const minStartDate = new Date()

    return (
      <div className={styles.container}>
        <LoaderOverlay show={isLoading} />
        <h1>
          <Translation text={partnerFlagRequestTranslation.content.title} span />
        </h1>
        <div className={styles.topBlock}>
          <DropdownDynamic
            caption="codeName"
            onChange={onProfilingCodeSelect}
            label={t(partnerFlagRequestTranslation.content.fields.selector)}
            source={source}
            renderFunction={renderDropdownFn as (item: IForm) => JSX.Element}
            value={selectedProfilingCode?.codeName || ''}
            fieldsToSearch={['codeName', 'code']}
          />
          <RenderWhen condition={Boolean(messages.length)}>
            <InfoBlock text={infoBlockContent} type={InfoBlockType.Warning} />
          </RenderWhen>
        </div>
        <div className={styles.bottomBlock}>
          <div className={styles.dates}>
            <DatePicker
              disabled={!isSelectedCodeAvailable}
              mode="single"
              onChange={setStartDate}
              fromDate={minStartDate}
              value={startDate}
              label={
                <Translation text={partnerFlagRequestTranslation.content.fields.startDate} span />
              }
            />
            <DatePicker
              disabled={!isSelectedCodeAvailable}
              mode="single"
              onChange={setEndDate}
              fromDate={startDate}
              value={endDate}
              label={
                <Translation text={partnerFlagRequestTranslation.content.fields.endDate} span />
              }
            />
          </div>
          <div className={styles.actions}>
            <Button color="danger" variant="text" onClick={onClose}>
              <Translation text={partnerFlagRequestTranslation.content.buttons.cancel} span />
            </Button>
            <Button onClick={onApplyClickHandler} disabled={isApplyButtonDisabled}>
              <Translation
                text={
                  inactivePartnerFlag
                    ? partnerFlagRequestTranslation.content.buttons.reactivate
                    : partnerFlagRequestTranslation.content.buttons.apply
                }
                span
              />
            </Button>
          </div>
        </div>
        {hasWarningCancel ? (
          <ConfirmDialog
            isVisible={isWarningVisible}
            onConfirm={handleWarningClose}
            onReject={handleWarningCancel}
            title={warningConfig?.title || ''}
            content={warningConfig?.content || ''}
            confirmBtnText={
              warningConfig?.confirmBtnText || partnerFlagRequestTranslation.confirm.button.confirm
            }
            rejectBtnText={warningConfig?.cancelBtnText || ''}
            size="sm"
          />
        ) : (
          <ConfirmDialog
            isVisible={isWarningVisible}
            onConfirm={handleWarningClose}
            title={warningConfig?.title || ''}
            content={warningConfig?.content || ''}
            confirmBtnText={
              warningConfig?.confirmBtnText || partnerFlagRequestTranslation.confirm.button.confirm
            }
            isOnlyApply
            size="sm"
          />
        )}
      </div>
    )
  }
)

PartnerFlagRequest.displayName = 'PartnerFlagRequest'
